import { useEffect, useState, useCallback } from "react";
import styles from "./Basket.module.scss";
import { useCurrencies } from "../../../context/CurrenciesContext";
import ProductsService from "../../../api/services/products.service";
import Loading from "../../../components/common/loading/Loading";
import { BasketTypes } from "../../../api/types/products.types";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import emptyBasketImage from "../../../assets/images/basket/empty_basket.png";
import { useAuth } from "../../../context/AuthContext";
import { useBasketWishlist } from "../../../context/BasketWishlistContext";
import LocalizedLink from "../../../components/common/localized-link/LocalizedLink";
import { useTranslation } from "react-i18next";
import { useLocalizedNavigate } from "../../../hooks/useLocalizedNavigate";
import { useLanguage } from "../../../context/LanguageContext";
import { getAttributeValue } from "../../../utils/attributeTranslations";
import { useBasketDelivery } from "../../../hooks/useBasketDelivery";
import { useInstallmentBanks } from "../../../hooks/useInstallmentBanks";
import InstallmentSummary, {
  InstallmentSelection,
} from "../../../components/sections/basket/installment-summary/InstallmentSummary";
import {
  readInstallmentSelection,
  writeInstallmentSelection,
} from "../../../utils/installmentSelection";

const Basket = () => {
  const [basketData, setBasketData] = useState<BasketTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingItems, setUpdatingItems] = useState<number[]>([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const { refreshCounts, setBasketTotalPrice } = useBasketWishlist();
  const { currencyId } = useCurrencies();
  const { isLoggedIn } = useAuth();
  const { t } = useTranslation("translation");
  const navigate = useLocalizedNavigate();
  const { currentLanguage } = useLanguage();
  const { delivery, refresh: refreshDelivery } = useBasketDelivery(currencyId);
  const installmentBanks = useInstallmentBanks();
  const [installment, setInstallment] = useState<InstallmentSelection | null>(
    null,
  );

  // Restore the plan chosen earlier in this session, so coming back from the
  // checkout does not lose it.
  useEffect(() => {
    const stored = readInstallmentSelection();
    if (!stored || !installmentBanks.length) return;

    const bank = installmentBanks.find((b) => b.id === stored.bankId);
    const term = bank?.terms.find((t) => t.id === stored.termId);
    if (bank && term) setInstallment({ bank, term });
  }, [installmentBanks]);

  // The checkout page reads the choice back out of session storage.
  const chooseInstallment = (selection: InstallmentSelection | null) => {
    setInstallment(selection);
    writeInstallmentSelection(
      selection
        ? { bankId: selection.bank.id, termId: selection.term.id }
        : null,
    );
  };

  const getActualPrice = (product: any): number => {
    if (product.discount_percent > 0) {
      if (product.discount_price && product.discount_price > 0) {
        return product.discount_price;
      }
      return product.price - (product.price * product.discount_percent) / 100;
    }
    return product.price;
  };

  const getDiscountInfo = (product: any) => {
    if (product.discount_percent > 0) {
      const discountedPrice =
        product.discount_price && product.discount_price > 0
          ? product.discount_price
          : product.price - (product.price * product.discount_percent) / 100;

      return {
        hasDiscount: true,
        originalPrice: product.price,
        discountedPrice: discountedPrice,
        discountPercent: product.discount_percent,
        saving: product.price - discountedPrice,
      };
    }
    return { hasDiscount: false };
  };

  const loadGuestBasket = useCallback(() => {
    const guestBasket = localStorage.getItem("guest_basket");
    if (guestBasket) {
      const parsedBasket = JSON.parse(guestBasket);
      const basketWithIds = parsedBasket.map((item: any, index: number) => ({
        ...item,
        id: item.product_variant_id,
      }));

      setBasketData(basketWithIds);
      const totalPrice = basketWithIds.reduce((total: any, item: any) => {
        const actualPrice = getActualPrice(item.product);
        return total + actualPrice * item.quantity;
      }, 0);
      setBasketTotalPrice(totalPrice);
    } else {
      setBasketData([]);
      setBasketTotalPrice(0);
    }
  }, [setBasketTotalPrice]);

  const loadUserBasket = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ProductsService.getAllBAsket(Number(currencyId));
      setBasketData(res.data);
      const totalPrice = res.data.reduce((total: any, item: any) => {
        const actualPrice = getActualPrice(item.product);
        return total + actualPrice * item.quantity;
      }, 0);
      setBasketTotalPrice(totalPrice);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currencyId, setBasketTotalPrice]);

  useEffect(() => {
    if (!currencyId) return;

    if (isLoggedIn) {
      loadUserBasket();
    } else {
      loadGuestBasket();
    }
  }, [currencyId, isLoggedIn, loadUserBasket, loadGuestBasket]);

  const toggleSelectItem = (id: number) => {
    setSelectedItems((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === basketData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(basketData.map((item) => item.product.id));
    }
  };

  const updateQuantity = async (id: number, changeAmount: number) => {
    if (isLoggedIn) {
      setUpdatingItems((prev) => [...prev, id]);
      try {
        const formData = new FormData();
        formData.append("product_variant_id", id.toString());
        formData.append("quantity", changeAmount.toString());
        await ProductsService.updateBasketAuth(Number(currencyId), formData);

        const updatedBasket = basketData.map((item) =>
          item.product.id === id
            ? { ...item, quantity: item.quantity + changeAmount }
            : item,
        );

        setBasketData(updatedBasket);
        const newTotalPrice = updatedBasket.reduce((total, item) => {
          const actualPrice = getActualPrice(item.product);
          return total + actualPrice * item.quantity;
        }, 0);
        setBasketTotalPrice(newTotalPrice);

        refreshCounts();
      } catch (error: any) {
        console.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            t("common_error"),
        );
        loadUserBasket();
      } finally {
        setUpdatingItems((prev) => prev.filter((itemId) => itemId !== id));
      }
    } else {
      const storedBasket = localStorage.getItem("guest_basket");
      if (storedBasket) {
        let basketData = JSON.parse(storedBasket);
        const itemIndex = basketData.findIndex(
          (item: any) => item.product_variant_id === id,
        );

        if (itemIndex !== -1) {
          const newQuantity = basketData[itemIndex].quantity + changeAmount;
          if (newQuantity < 1) return;
          basketData[itemIndex].quantity = newQuantity;
          localStorage.setItem("guest_basket", JSON.stringify(basketData));
          const updatedBasket = basketData.map((item: any, index: number) => ({
            ...item,
            id: item.product_variant_id,
          }));
          setBasketData(updatedBasket);
          const newTotalPrice = updatedBasket.reduce(
            (total: any, item: any) => {
              const actualPrice = getActualPrice(item.product);
              return total + actualPrice * item.quantity;
            },
            0,
          );
          setBasketTotalPrice(newTotalPrice);

          refreshCounts();
        }
      }
    }
  };

  const removeItem = async (id: number) => {
    if (isLoggedIn) {
      setUpdatingItems((prev) => [...prev, id]);
      try {
        const formData = new FormData();
        formData.append("product_variant_id", id.toString());
        await ProductsService.removeBasket()(formData);

        const updatedBasket = basketData.filter(
          (item) => item.product.id !== id,
        );
        setBasketData(updatedBasket);
        const newTotalPrice = updatedBasket.reduce((total, item) => {
          const actualPrice = getActualPrice(item.product);
          return total + actualPrice * item.quantity;
        }, 0);
        setBasketTotalPrice(newTotalPrice);
        refreshCounts();
      } catch (error: any) {
        console.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            t("common_error"),
        );
        loadUserBasket();
      } finally {
        setUpdatingItems((prev) => prev.filter((itemId) => itemId !== id));
      }
    } else {
      const storedBasket = localStorage.getItem("guest_basket");
      if (storedBasket) {
        let basketData = JSON.parse(storedBasket);
        basketData = basketData.filter(
          (item: any) => item.product_variant_id !== id,
        );
        localStorage.setItem("guest_basket", JSON.stringify(basketData));
        const updatedBasket = basketData.map((item: any, index: number) => ({
          ...item,
          id: item.product_variant_id,
        }));
        refreshCounts();
        setBasketData(updatedBasket);
        const newTotalPrice = updatedBasket.reduce((total: any, item: any) => {
          const actualPrice = getActualPrice(item.product);
          return total + actualPrice * item.quantity;
        }, 0);
        setBasketTotalPrice(newTotalPrice);
      }
    }
  };

  const clearBasket = async () => {
    if (isLoggedIn) {
      try {
        await ProductsService.clearBasket()();
        setBasketData([]);
        setBasketTotalPrice(0);
        refreshCounts();
      } catch (error: any) {
        console.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            t("common_error"),
        );
      }
    } else {
      localStorage.removeItem("guest_basket");
      setBasketData([]);
      setBasketTotalPrice(0);
      refreshCounts();
    }
  };

  const getTotalPrice = () => {
    return basketData.reduce((total, item) => {
      const actualPrice = getActualPrice(item.product);
      return total + actualPrice * item.quantity;
    }, 0);
  };

  // Crossing the free-delivery threshold flips the fee to zero, so the quote is
  // re-read whenever the basket total moves.
  useEffect(() => {
    refreshDelivery();
  }, [basketData, refreshDelivery]);

  const deliveryFee = delivery?.delivery_fee ?? 0;

  const getOrderTotal = () => getTotalPrice() + deliveryFee;

  // Amounts for the chosen instalment plan, derived from the order total as it
  // stands rather than remembered at the moment of choosing, so a quantity
  // change moves them too.
  //
  // A plan with a markup makes the order itself cost more: the buyer owes the
  // full marked-up sum, and it is the bank they then repay month by month. So
  // the markup lands on the order total, and the monthly figure is a note
  // about the bank rather than the amount being charged here.
  const installmentTotal = installment
    ? getOrderTotal() * (1 + installment.term.markup_percent / 100)
    : 0;
  const installmentFee = installment ? installmentTotal - getOrderTotal() : 0;
  const installmentMonthly = installment
    ? installmentTotal / installment.term.months
    : 0;

  const getTotalItems = () => {
    return basketData.reduce((total, item) => total + item.quantity, 0);
  };

  const handleConfrim = () => {
    if (!isLoggedIn) {
      navigate("/auth/login");
    } else {
      navigate("/complete-order");
    }
  };

  const getLocalizedName = (product: BasketTypes) => {
    switch (currentLanguage) {
      case "az":
        return product?.product?.name_az || product?.product?.name_en;
      case "de":
        return product?.product?.name_de || product?.product?.name_en;
      case "es":
        return product?.product?.name_es || product?.product?.name_en;
      case "en":
      default:
        return product?.product?.name_en;
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (basketData.length === 0) {
    return (
      <div className={styles.basket}>
        <div className="container">
          <div className={styles.emptyBasket}>
            <div className={styles.emptyBasketIcon}>
              <img src={emptyBasketImage} alt="Empty Basket" />
            </div>
            <h2>{t("basket.empty_basket.title") || "Səbət boşdur!"}</h2>
            <p>
              {t("basket.empty_basket.description") ||
                "İstədiyiniz məhsulları səbətinizə əlavə edin"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.basket}>
      <TitleUpdater title={t("basket.title")} />
      <div className="container">
        <div className={styles.basketHeader}>
          <h1 className={styles.title}>{t("basket.title")}</h1>
        </div>

        <div className={styles.basketContent}>
          <div className={styles.itemsSection}>
            <div className={styles.selectAllRow}>
              <div className={styles.selectAllGroup}>
                <input
                  type="checkbox"
                  checked={
                    selectedItems.length === basketData.length &&
                    basketData.length > 0
                  }
                  onChange={toggleSelectAll}
                  className={styles.checkbox}
                />
                <span className={styles.selectAllText}>
                  {t("basket.select_all")}
                </span>
              </div>
              <button className={styles.clearAllBtn} onClick={clearBasket}>
                {t("basket.actions.clear_all")}
              </button>
            </div>
            {basketData.map((item, idx) => {
              const discountInfo = getDiscountInfo(item.product);

              return (
                <div key={idx} className={styles.basketItem}>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.product.id)}
                    onChange={() => toggleSelectItem(item.product.id)}
                    className={styles.checkbox}
                  />
                  <div className={styles.itemImage}>
                    <img
                      src={
                        item.product.images ? item.product.images[0]?.image : ""
                      }
                      alt=""
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/images/placeholder.jpg";
                      }}
                    />
                  </div>

                  <div className={styles.itemDetails}>
                    <div className={styles.itemHeader}>
                      <LocalizedLink
                        to={`/product/${item.product.id}`}
                        className={styles.productName}
                      >
                        {getLocalizedName(item)}
                      </LocalizedLink>
                    </div>

                    <div className={styles.priceRow}>
                      {discountInfo.hasDiscount ? (
                        <div className={styles.priceGroup}>
                          <span className={styles.currentPrice}>
                            {discountInfo.discountedPrice.toFixed(2)}{" "}
                            {item.product.currency_name}
                          </span>
                          <span className={styles.originalPrice}>
                            {discountInfo.originalPrice.toFixed(2)}{" "}
                            {item.product.currency_name}
                          </span>
                        </div>
                      ) : (
                        <span className={styles.currentPrice}>
                          {item.product.price.toFixed(2)}{" "}
                          {item.product.currency_name}
                        </span>
                      )}
                    </div>

                    <div className={styles.metaRow}>
                      <div className={styles.attributes}>
                        {item.product.attributes &&
                          item.product.attributes
                            .filter((attr: any) => attr.is_view)
                            .map((attribute) => (
                              <span
                                key={attribute.id}
                                className={styles.attribute}
                              >
                                {getAttributeValue(attribute)}
                              </span>
                            ))}
                      </div>
                      <div className={styles.sellerInfo}>
                        <span className={styles.stockBadge}>
                          {item.product.stock_quantity > 0 ? (
                            <>
                              {t("basket.product.in_stock")}
                              {item.product.stock_quantity}
                            </>
                          ) : (
                            <span className={styles.no_stock}>
                              {t("no_stock") || "Pulsuz çatdırılma"}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.itemControlsWrapper}>
                    <div className={styles.quantityControl}>
                      <button
                        className={styles.quantityBtn}
                        onClick={() => updateQuantity(item.product.id, -1)}
                        disabled={
                          item.quantity <= 1 ||
                          updatingItems.includes(item.product.id)
                        }
                      >
                        -
                      </button>
                      <span className={styles.quantity}>
                        {updatingItems.includes(item.product.id) ? (
                          <div className={styles.loading_spinner_small}></div>
                        ) : (
                          item.quantity
                        )}
                      </span>
                      <button
                        className={styles.quantityBtn}
                        onClick={() => updateQuantity(item.product.id, 1)}
                        disabled={
                          item.quantity >= item.product.stock_quantity ||
                          updatingItems.includes(item.product.id)
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <button
                    className={styles.removeBtn}
                    onClick={() => removeItem(item.product.id)}
                    disabled={updatingItems.includes(item.product.id)}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          <div className={styles.summarySection}>
            <div className={styles.orderSummary}>
              <h3>{t("basket.order_summary.title")}</h3>

              <div className={styles.summaryRow}>
                <span>{t("basket.order_summary.products")}</span>
                <span>
                  {getTotalItems()} {t("basket.order_summary.products_count")}
                </span>
              </div>

              <div className={styles.summaryRow}>
                <span>{t("basket.order_summary.amount")}</span>
                <span className={styles.totalAmount}>
                  {getTotalPrice().toFixed(2)}{" "}
                  {basketData[0]?.product.currency_name}
                </span>
              </div>

              {delivery && delivery.delivery_fee_full > 0 && (
                <>
                  <div className={styles.summaryRow}>
                    <span>{t("basket.order_summary.shipping")}</span>
                    {delivery.is_free_delivery ? (
                      <span className={styles.freeDelivery}>
                        {t("basket.order_summary.free_shipping")}
                      </span>
                    ) : (
                      <span className={styles.totalAmount}>
                        {delivery.delivery_fee.toFixed(2)}{" "}
                        {delivery.currency_name}
                      </span>
                    )}
                  </div>

                  {delivery.free_delivery_threshold > 0 && (
                    <p className={styles.deliveryNote}>
                      {delivery.is_free_delivery
                        ? t("basket.order_summary.delivery_free_note", {
                            amount: delivery.free_delivery_threshold.toFixed(2),
                            currency: delivery.currency_name,
                          })
                        : t("basket.order_summary.delivery_hint", {
                            amount: delivery.free_delivery_threshold.toFixed(2),
                            currency: delivery.currency_name,
                          })}
                    </p>
                  )}
                </>
              )}

              <InstallmentSummary
                banks={installmentBanks}
                total={getOrderTotal()}
                currency={basketData[0]?.product.currency_name}
                selection={installment}
                onSelect={chooseInstallment}
              />

              {installment && installmentFee > 0 && (
                <div className={styles.summaryRow}>
                  <span>
                    {t("installments.fee", {
                      percent: installment.term.markup_percent,
                    })}
                  </span>
                  <span className={styles.totalAmount}>
                    {installmentFee.toFixed(2)}{" "}
                    {basketData[0]?.product.currency_name}
                  </span>
                </div>
              )}

              <hr className={styles.divider} />

              <div className={styles.summaryRow}>
                <p className={styles.finalTotal}>
                  {t("basket.order_summary.order_total")}
                </p>
                <span className={styles.totalAmount}>
                  {(installment ? installmentTotal : getOrderTotal()).toFixed(
                    2,
                  )}{" "}
                  {basketData[0]?.product.currency_name}
                </span>
              </div>

              {installment && (
                <p className={styles.installmentNote}>
                  {t("installments.bank_note", {
                    bank: installment.bank.name,
                    months: installment.term.months,
                    amount: installmentMonthly.toFixed(2),
                    currency: basketData[0]?.product.currency_name,
                  })}
                </p>
              )}

              <button className={styles.checkoutBtn} onClick={handleConfrim}>
                {isLoggedIn
                  ? t("basket.actions.confirm_order")
                  : t("basket.actions.login_to_checkout")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Basket;
